import { Component, Event, EventEmitter, h, Prop, State } from '@stencil/core';

@Component({
  tag: 'biel-header',
  styleUrl: 'biel-header.css',
  shadow: true,
})
export class BielHeader {
  @Prop() headerTitle: string;
  @Prop() project: string;
  @Prop() hideCloseButton: boolean;
  @Prop() hideExpandButton: boolean;
  @Prop() hideRefreshButton: boolean;
  @Prop() hideThinkModeButton: boolean = false;
  @Prop() hideTooltips: boolean = false;
  @Prop() showPoweredBy: boolean = true;
  @Prop() fullscreen: boolean;
  @Prop() bielId: string;
  @Prop() thinkModeEnabled: boolean = false;
  @Prop() refreshButtonText: string = 'Refresh';
  @Prop() expandButtonText: string = 'Expand';
  @Prop() collapseButtonText: string = 'Collapse';
  @Prop() closeButtonText: string = 'Close';
  @Prop() thinkModeButtonText: string = 'Enable think mode (better quality, takes longer)';

  @State() isFullscreen: boolean;
  @State() isThinkModeActive: boolean;

  @Event() closeClicked: EventEmitter<{ bielId: string }>;
  @Event() fullscreenClicked: EventEmitter<{ bielId: string }>;
  @Event() refreshClicked: EventEmitter<{ bielId: string }>;
  @Event() thinkModeToggled: EventEmitter<{ bielId: string; isActive: boolean }>;

  componentWillLoad() {
    this.isFullscreen = this.fullscreen;
    this.isThinkModeActive = this.thinkModeEnabled;
  }

  handleCloseClick() {
    this.closeClicked.emit({ bielId: this.bielId });
  }

  handleFullscreenClick() {
    this.isFullscreen = !this.isFullscreen;
    this.fullscreenClicked.emit({ bielId: this.bielId });
  }

  handleRefreshClick() {
    this.refreshClicked.emit({ bielId: this.bielId });
  }

  handleThinkModeClick() {
    this.isThinkModeActive = !this.isThinkModeActive;
    this.thinkModeToggled.emit({ bielId: this.bielId, isActive: this.isThinkModeActive });
  }

  render() {
    return (
      <header class="biel-header">
        <div class="biel-header__title-container">
          <div class="biel-header__title">{this.headerTitle}</div>
          <div class="biel-header__powered-by">
            {this.showPoweredBy && (
              <span>
                Powered by{' '}
                <a target="_blank" href="https://biel.ai">
                  biel.ai
                </a>
              </span>
            )}
          </div>
        </div>

        <div class="biel-header__icons">
          {!this.hideThinkModeButton && (
            <button
              class={`biel-header__icon ${this.isThinkModeActive ? 'biel-header__icon--active' : ''}`}
              onClick={() => this.handleThinkModeClick()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-lightbulb"
              >
                <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                <path d="M9 18h6" />
                <path d="M10 22h4" />
              </svg>
              {!this.hideTooltips && <span class="biel-header__tooltip">{this.thinkModeButtonText}</span>}
            </button>
          )}
          {!this.hideRefreshButton && (
            <button
              class="biel-header__icon"
              onClick={() => this.handleRefreshClick()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-refresh-cw"
              >
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M8 16H3v5" />
              </svg>
              {!this.hideTooltips && <span class="biel-header__tooltip">{this.refreshButtonText}</span>}
            </button>
          )}
          {!this.hideExpandButton && (
            <button
              class="biel-header__icon"
              onClick={() => this.handleFullscreenClick()}
            >
              {this.isFullscreen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-minimize-2"
                >
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="14" x2="21" y1="10" y2="3" />
                  <line x1="3" x2="10" y1="21" y2="14" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-maximize-2"
                >
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" x2="14" y1="3" y2="10" />
                  <line x1="3" x2="10" y1="21" y2="14" />
                </svg>
              )}
              {!this.hideTooltips && <span class="biel-header__tooltip">{this.isFullscreen ? this.collapseButtonText : this.expandButtonText}</span>}
            </button>
          )}
          {!this.hideCloseButton && (
            <button class="biel-header__icon" onClick={() => this.handleCloseClick()}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-x"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
              {!this.hideTooltips && <span class="biel-header__tooltip">{this.closeButtonText}</span>}
            </button>
          )}
        </div>
      </header>
    );
  }
}
